import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api } from "@/lib/api";
import { coverUrl } from "@/lib/cover";
import { fadeIn, fadeOutClone, type FadeHandle } from "@/lib/crossfade";
import { deviceFingerprint } from "@/lib/deviceIdentity";
import { addDjHistory, getDjHistory } from "@/lib/djHistory";
import { djVoiceEnabled, getDjLine, speakDjLine, stopDjVoice, waitPlaying } from "@/lib/djVoice";
import { addNotInterested, getNotInterested } from "@/lib/notInterested";
import { assignSourceToAudio } from "@/lib/playback";
import type { Track } from "@/types/types";

type RepeatMode = "off" | "one" | "all";

interface PlayerCtx {
  current: Track | null;
  queue: Track[];
  index: number;
  playing: boolean;
  duration: number;
  volume: number;
  repeat: RepeatMode;
  shuffle: boolean;
  play: (queue: Track[], startIndex?: number) => Promise<void>;
  playOne: (t: Track) => Promise<void>;
  /** Same as play(), but flags the queue as an infinite DJ mix — when we
   *  approach the end, more tracks get fetched and appended automatically. */
  playDJMix: (tracks: Track[]) => Promise<void>;
  toggle: () => void;
  next: () => void;
  prev: () => void;
  seek: (sec: number) => void;
  setVolume: (v: number) => void;
  setRepeat: (m: RepeatMode) => void;
  setShuffle: (s: boolean) => void;
  /** Spotify-style smart shuffle: off -> shuffle -> smart shuffle -> off.
   *  Smart shuffle slips a recommended track (✦) in every 3 songs. */
  smartShuffle: boolean;
  cycleShuffle: () => void;
  playSmartShuffle: (tracks: Track[]) => Promise<void>;
  isRecommended: (id: string) => boolean;
  /** Drop a smart-shuffle recommendation (skips it if it's playing) and
   *  never suggest it again. */
  removeRecommended: (id: string) => void;
  /** What actually plays next, in play order (shuffle/smart aware). */
  upNext: Track[];
  enqueue: (t: Track) => void;
  crossfadeMs: number;
  setCrossfadeMs: (n: number) => void;
}

export const PlayerContext = createContext<PlayerCtx | null>(null);

// Playback position ticks up to 4x/sec (throttled — see the timeupdate
// listener below). It lives in its own context so that the ~15 usePlayer()
// consumers that never render a live position (TrackRow, QuickPickCard,
// Playbar shell, QueueDrawer, ...) don't re-render every 250ms just because
// the single shared context object's identity changed. Only ProgressBar and
// KaraokeView actually need this value.
export const PlayerProgressContext = createContext<number>(0);

// Remember the last track played (and its position) so it's ready to hit
// play on immediately after a page reload / app restart, resuming right
// where it was left — like Spotify. Never auto-plays without a user
// gesture though, since browsers block that anyway.
const RESTORE_KEY = "sl.lastTrack";
interface RestoredState { track: Track; positionSec: number }
function loadRestoredState(): RestoredState | null {
  try {
    const raw = localStorage.getItem(RESTORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && parsed.track ? (parsed as RestoredState) : null;
  } catch { return null; }
}

// After sitting idle for a while (tab backgrounded, computer asleep), the
// browser's pooled connection to the API can go stale — the next request
// reusing it just hangs with no error, since nothing tells the browser the
// socket is dead. el.play() then never resolves or rejects, so the user's
// click on play silently does nothing forever (previously only a hard
// reload — fresh connections — fixed it). Race it against a timeout so a
// stuck load can be detected and retried with a forced-fresh connection.
const PLAY_TIMEOUT_MS = 7000;
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error("timeout")), ms);
    p.then((v) => { window.clearTimeout(t); resolve(v); },
           (e) => { window.clearTimeout(t); reject(e); });
  });
}

function shuffled<T>(a: T[]): T[] {
  const arr = a.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [restoreInit] = useState(() => {
    const r = loadRestoredState();
    return { queue: r ? [r.track] : [], order: r ? [0] : [], positionSec: r?.positionSec ?? 0 };
  });
  const [queue, setQueue] = useState<Track[]>(restoreInit.queue); // original order
  const [order, setOrder] = useState<number[]>(restoreInit.order); // indices, shuffled or not
  const [pos, setPos] = useState(0);                          // position in `order`
  // set on mount when a track was restored from localStorage — tells
  // loadCurrent to load the source (seeked to restorePositionRef) without
  // auto-playing it
  const isRestoringRef = useRef(restoreInit.queue.length > 0);
  const restorePositionRef = useRef(restoreInit.positionSec);
  const [reloadTick, bumpReload] = useState(0);              // forces re-load when pos didn't change
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVol] = useState(() => Number(localStorage.getItem("sl.vol") ?? 0.9));
  const [repeat, setRepeat] = useState<RepeatMode>(() => (localStorage.getItem("sl.repeat") as RepeatMode) || "off");
  const [shuffle, setShuffleState] = useState<boolean>(() => localStorage.getItem("sl.shuffle") === "1");
  const [smartShuffle, setSmartState] = useState<boolean>(() =>
    localStorage.getItem("sl.shuffle") === "1" && localStorage.getItem("sl.smartShuffle") === "1");
  const [recIds, setRecIds] = useState<Set<string>>(() => new Set());
  const [crossfadeMs, setCrossfadeMsState] = useState<number>(() =>
    Math.max(0, Number(localStorage.getItem("sl.crossfadeMs") ?? 0)));
  const listenSentRef = useRef(0);
  const fadeOutRef = useRef<FadeHandle | null>(null);
  const fadeInRef = useRef<FadeHandle | null>(null);
  const crossfadedRef = useRef<string | null>(null);  // track id we've already started fading TO
  const wantCrossfadeRef = useRef(false);              // set on scheduled early advance so loadCurrent uses fadeIn

  const setCrossfadeMs = useCallback((n: number) => {
    setCrossfadeMsState(Math.max(0, Math.min(12000, n)));
    try { localStorage.setItem("sl.crossfadeMs", String(n)); } catch {}
  }, []);

  const index = order[pos] ?? 0;
  const current = queue[index] ?? null;

  // audio element created once
  useEffect(() => {
    const el = new Audio();
    el.preload = "auto";
    el.volume = volume;
    // Enable native iOS/macOS AirPlay picker in Safari + CarPlay handoff.
    el.setAttribute("x-webkit-airplay", "allow");
    // Play in the background media session queue (iOS lockscreen).
    el.setAttribute("playsinline", "true");
    audioRef.current = el;
    // Throttle progress state updates to ~4 Hz. Native timeupdate fires
    // 4-15x/s and every push here re-renders every usePlayer consumer
    // (NowPlaying, Marquee, ProgressBar, Playbar, QueueDrawer). On mobile
    // Safari + Bluetooth (higher I/O latency on AirPods) that main-thread
    // pressure was starving the audio pipeline enough to cause micro-freezes.
    let lastPushed = -1;
    el.addEventListener("timeupdate", () => {
      const now = el.currentTime;
      if (Math.abs(now - lastPushed) >= 0.25) {
        lastPushed = now;
        setProgress(now);
      }
    });
    el.addEventListener("loadedmetadata", () => setDuration(el.duration));
    el.addEventListener("play", () => setPlaying(true));
    el.addEventListener("pause", () => setPlaying(false));
    el.addEventListener("ended", () => {
      // read latest state via refs to avoid stale closure
      endedRef.current?.();
    });
    return () => { el.pause(); el.src = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = audioRef.current; if (!el) return;
    el.volume = volume;
    localStorage.setItem("sl.vol", String(volume));
  }, [volume]);

  useEffect(() => { localStorage.setItem("sl.repeat", repeat); }, [repeat]);
  useEffect(() => { localStorage.setItem("sl.shuffle", shuffle ? "1" : "0"); }, [shuffle]);
  useEffect(() => { localStorage.setItem("sl.smartShuffle", smartShuffle ? "1" : "0"); }, [smartShuffle]);

  // latest-value refs for async work (smart shuffle injection, DJ voice)
  const queueRef = useRef(queue);
  const orderRef = useRef(order);
  const posRef = useRef(pos);
  const volumeRef = useRef(volume);
  const recIdsRef = useRef(recIds);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { orderRef.current = order; }, [order]);
  useEffect(() => { posRef.current = pos; }, [pos]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { recIdsRef.current = recIds; }, [recIds]);

  const loadCurrent = useCallback(async () => {
    const el = audioRef.current; if (!el || !current) return;
    try {
      // Restoring the last-played track on mount: load the source so it's
      // ready to go, but don't auto-play — browsers block that without a
      // user gesture anyway, and we want it to start from 0 on demand.
      if (isRestoringRef.current) {
        isRestoringRef.current = false;
        await assignSourceToAudio(el, current);
        const seekTo = restorePositionRef.current;
        if (seekTo > 0.5) {
          const applySeek = () => {
            try { el.currentTime = el.duration > 0 ? Math.min(seekTo, el.duration - 0.25) : seekTo; }
            catch { /* ignore */ }
            el.removeEventListener("loadedmetadata", applySeek);
          };
          el.addEventListener("loadedmetadata", applySeek);
        }
        setProgress(seekTo);
        setPlaying(false);
        if ("mediaSession" in navigator) {
          const coverId = current.album?.cover_id;
          const artwork = coverId
            ? [96, 192, 256, 384, 512].map((s) => ({
                src: new URL(coverUrl(coverId, s), window.location.origin).toString(),
                sizes: `${s}x${s}`, type: "image/jpeg",
              }))
            : undefined;
          navigator.mediaSession.metadata = new MediaMetadata({
            title: current.title, artist: current.artist?.name,
            album: current.album?.title, artwork,
          });
        }
        return;
      }
      // If a crossfade is in progress, split the current stream into a
      // detached fade-out clone so it keeps playing while we load the next
      // track on the main element (which will then fade in from 0).
      const doFade = wantCrossfadeRef.current && crossfadeMs > 0 && !el.paused && el.duration > 0;
      wantCrossfadeRef.current = false;
      let userVol = volume;
      if (doFade) {
        fadeOutRef.current?.cancel();
        fadeOutRef.current = fadeOutClone(el, userVol, crossfadeMs);
      }
      await assignSourceToAudio(el, current);
      if (doFade) el.volume = 0;
      try {
        await withTimeout(el.play(), PLAY_TIMEOUT_MS);
      } catch (playErr) {
        // Likely a stale connection reused after being idle, not a real
        // playback error — drop it and force one fresh retry.
        console.warn("playback stalled, retrying with a fresh connection", playErr);
        el.pause();
        el.removeAttribute("src");
        el.load();
        await assignSourceToAudio(el, current);
        if (doFade) el.volume = 0;
        try {
          await withTimeout(el.play(), PLAY_TIMEOUT_MS);
        } catch (retryErr) {
          // Even the retry failed — restore volume so a future toggle() plays audibly.
          el.volume = userVol;
          throw retryErr;
        }
      }
      setPlaying(true);
      setProgress(0);
      listenSentRef.current = 0;
      if (doFade) {
        fadeInRef.current?.cancel();
        fadeInRef.current = fadeIn(el, userVol, crossfadeMs);
      } else {
        el.volume = userVol;
      }
      if ("mediaSession" in navigator) {
        // Push the album cover as artwork — without it iOS/Android lockscreen
        // + notification center fall back on the site favicon.
        const coverId = current.album?.cover_id;
        const artwork = coverId
          ? [96, 192, 256, 384, 512].map((s) => {
              const src = coverUrl(coverId, s);
              const type = src.startsWith("http") ? "image/jpeg" : "image/jpeg";
              return { src: new URL(src, window.location.origin).toString(),
                       sizes: `${s}x${s}`, type };
            })
          : undefined;
        navigator.mediaSession.metadata = new MediaMetadata({
          title: current.title,
          artist: current.artist?.name,
          album: current.album?.title,
          artwork,
        });
      }
    } catch (e) { console.warn("play failed", e); setPlaying(false); }
  }, [current]);

  useEffect(() => { if (current) { crossfadedRef.current = null; void loadCurrent(); } },
             [current, loadCurrent, reloadTick]);

  // Persist {track, position} so a restart resumes right where playback
  // left off (Spotify-style) — on an interval while a track is loaded, on
  // pause, and on tab close (interval alone could miss the final position).
  useEffect(() => {
    const el = audioRef.current; if (!el || !current) return;
    const persist = () => {
      try { localStorage.setItem(RESTORE_KEY, JSON.stringify({ track: current, positionSec: el.currentTime })); }
      catch {}
    };
    persist();
    const t = window.setInterval(persist, 5000);
    el.addEventListener("pause", persist);
    window.addEventListener("beforeunload", persist);
    return () => {
      window.clearInterval(t);
      el.removeEventListener("pause", persist);
      window.removeEventListener("beforeunload", persist);
    };
  }, [current]);

  // Schedule an early advance() when we're about to hit the crossfade window.
  useEffect(() => {
    if (crossfadeMs <= 0 || !current) return;
    // Guards contre le cascade infini :
    //  - duration doit être connue (>0) et > 2× la fenêtre crossfade
    //  - progress doit être au delà de la fenêtre crossfade (donc pas juste chargé)
    //  - un delta minimal de 3s de lecture effective
    const cfSec = crossfadeMs / 1000;
    if (!Number.isFinite(duration) || duration < cfSec * 2 + 1) return;
    if (progress < Math.max(cfSec, 3)) return;
    const remaining = (duration - progress) * 1000;
    if (remaining <= crossfadeMs + 150 && remaining > 0
        && crossfadedRef.current !== current.id) {
      // only crossfade if there's actually a next track in the queue
      const nextPos = pos + 1;
      if (nextPos < order.length) {
        crossfadedRef.current = current.id;
        wantCrossfadeRef.current = true;
        // advance the queue — loadCurrent will pick up wantCrossfadeRef and fade
        setPos(nextPos);
      }
    }
  }, [progress, duration, current, crossfadeMs, pos, order.length]);

  // ---- queue prefetch: warm the resolver + disk cache for the next 2 tracks ----
  useEffect(() => {
    if (!current || queue.length === 0) return;
    const upcoming: string[] = [];
    for (let step = 1; step <= 2; step++) {
      const nextPos = pos + step;
      if (nextPos >= order.length) break;
      const t = queue[order[nextPos]];
      if (t) upcoming.push(t.id);
    }
    if (!upcoming.length) return;
    const ctrl = new AbortController();
    void Promise.all(upcoming.map((id) =>
      api(`/api/stream/${id}/prefetch`, { method: "POST", signal: ctrl.signal }).catch(() => {}),
    ));
    return () => ctrl.abort();
  }, [current, queue, order, pos]);

  // listen reporter (best-effort)
  useEffect(() => {
    const el = audioRef.current; if (!el || !current) return;
    const t = window.setInterval(() => {
      if (!el.paused && el.currentTime * 1000 - listenSentRef.current > 15000) {
        listenSentRef.current = el.currentTime * 1000;
        void api("/api/stats/listen", {
          method: "POST",
          body: JSON.stringify({
            track_id: current.id,
            listened_ms: Math.floor(el.currentTime * 1000),
            device_id: deviceFingerprint(),
          }),
        }).catch(() => {});
      }
    }, 5000);
    return () => window.clearInterval(t);
  }, [current]);

  /** Marks the current queue as an infinite source we can refill from. Reset
   *  to null on any explicit new play() so a random album click doesn't
   *  accidentally trigger DJ refills. */
  const queueSourceRef = useRef<"dj" | null>(null);
  const refillingRef = useRef(false);

  const smartGenRef = useRef(0);

  /** Fetch tracks that fit `base` and slot one in after every 3 upcoming
   *  songs. A newer play()/toggle bumps smartGenRef so stale answers drop. */
  const injectSmart = useCallback(async (base: Track[]) => {
    const gen = ++smartGenRef.current;
    if (base.length < 4) return;
    const seeds = shuffled(base).slice(0, 40).map((t) => t.id);
    const exclude = Array.from(new Set([...base.map((t) => t.id), ...getNotInterested()])).slice(0, 2500);
    let recs: Track[] = [];
    try {
      recs = await api<Track[]>("/api/recommend/for-tracks", {
        method: "POST",
        body: JSON.stringify({
          track_ids: seeds, exclude_ids: exclude,
          limit: Math.min(30, Math.max(4, Math.ceil(base.length / 3))),
        }),
      });
    } catch (e) { console.warn("smart shuffle failed", e); return; }
    if (gen !== smartGenRef.current || !recs?.length) return;
    const q = queueRef.current;
    const o = orderRef.current;
    const p = posRef.current;
    const start = q.length;
    const out = o.slice(0, p + 1);
    let used = 0;
    o.slice(p + 1).forEach((idx, i) => {
      out.push(idx);
      if (i % 3 === 2 && used < recs.length) out.push(start + used++);
    });
    if (!used) return;
    const placed = recs.slice(0, used);
    setQueue([...q, ...placed]);
    setOrder(out);
    setRecIds((prev) => new Set([...prev, ...placed.map((t) => t.id)]));
  }, []);

  const play = useCallback(async (q: Track[], start = 0) => {
    if (!q.length) return;
    queueSourceRef.current = null;
    smartGenRef.current++;
    setRecIds(new Set());
    setQueue(q);
    const seq = shuffle ? [start, ...shuffled(q.map((_, i) => i).filter((i) => i !== start))]
                        : q.map((_, i) => i);
    setOrder(seq);
    setPos(shuffle ? 0 : start);
    // Force reload même si le track cliqué est référentiellement identique
    // au `current` précédent (ex: re-cliquer même row / carte QuickPick).
    bumpReload((x) => x + 1);
    if (shuffle && smartShuffle) void injectSmart(q);
  }, [shuffle, smartShuffle, injectSmart]);

  const playSmartShuffle = useCallback(async (q: Track[]) => {
    if (!q.length) return;
    queueSourceRef.current = null;
    setShuffleState(true);
    setSmartState(true);
    setRecIds(new Set());
    setQueue(q);
    setOrder(shuffled(q.map((_, i) => i)));
    setPos(0);
    bumpReload((x) => x + 1);
    void injectSmart(q);
  }, [injectSmart]);

  const playOne = useCallback(async (t: Track) => play([t], 0), [play]);

  const playDJMix = useCallback(async (tracks: Track[]) => {
    if (!tracks.length) return;
    smartGenRef.current++;
    setRecIds(new Set());
    setQueue(tracks);
    setOrder(tracks.map((_, i) => i));
    setPos(0);
    queueSourceRef.current = "dj";
  }, []);

  // Infinite DJ refill — when we're within 3 tracks of the end of a DJ
  // queue, silently pull ~20 more from the backend and append. Excludes
  // everything already in the current queue so the mix never repeats.
  useEffect(() => {
    if (queueSourceRef.current !== "dj") return;
    if (!order.length || refillingRef.current) return;
    const remaining = order.length - pos - 1;
    if (remaining > 3) return;
    const ctrl = new AbortController();
    refillingRef.current = true;
    (async () => {
      try {
        type Banger = {
          id: string; title: string; duration_ms: number; popularity: number;
          artist: { id: string; name: string };
          album: { id: string; title: string; image_url: string | null };
        };
        const excludeIds = Array.from(new Set([...getDjHistory(), ...queue.map((t) => t.id)]));
        const url = `/api/recommend/bangers-for-me?limit=20&exclude=${encodeURIComponent(excludeIds.join(","))}`;
        const rows = await api<Banger[]>(url, { signal: ctrl.signal });
        if (!rows?.length) return;
        const asTracks: Track[] = rows
          .filter((b) => !queue.some((q) => q.id === b.id))
          .map((b) => ({
            id: b.id, title: b.title, disc: 1, track_no: 0,
            duration_ms: b.duration_ms, explicit: false, codec: null, bitrate_kbps: null,
            artist: { id: b.artist.id, name: b.artist.name, cover_id: null, image_url: null },
            album: {
              id: b.album.id, title: b.album.title, year: null,
              cover_id: b.album.image_url, image_url: b.album.image_url,
              artist: { id: b.artist.id, name: b.artist.name, cover_id: null, image_url: null },
            },
          } as Track));
        if (!asTracks.length) return;
        addDjHistory(asTracks.map((t) => t.id));
        const startIdx = queue.length;
        setQueue((prev) => [...prev, ...asTracks]);
        setOrder((prev) => [...prev, ...asTracks.map((_, i) => startIdx + i)]);
      } catch (e) {
        console.warn("DJ refill failed", e);
      } finally {
        refillingRef.current = false;
      }
    })();
    return () => ctrl.abort();
  }, [pos, order.length, queue]);

  // Voice DJ: talk over the first track of a DJ session, then every 4th
  // one. The next spoken line is requested one track ahead so the voice is
  // ready the moment its track starts.
  useEffect(() => {
    if (queueSourceRef.current !== "dj" || !current || !djVoiceEnabled()) return;
    const el = audioRef.current; if (!el) return;
    const speaksAt = (p: number) => p === 0 || p % 4 === 0;
    for (let p = pos + 1; p <= pos + 4 && p < order.length; p++) {
      if (speaksAt(p)) { const t = queue[order[p]]; if (t) void getDjLine(t.id, false); break; }
    }
    if (!speaksAt(pos)) return;
    let cancelled = false;
    const line = getDjLine(current.id, pos === 0);
    void (async () => {
      const [l, ok] = await Promise.all([line, waitPlaying(el)]);
      if (cancelled || !l || !ok) return;
      // let a crossfade finish first, both would fight over el.volume
      if (crossfadeMs > 0) await new Promise((r) => window.setTimeout(r, crossfadeMs));
      if (!cancelled && !el.paused) void speakDjLine(el, () => volumeRef.current, l);
    })();
    return () => { cancelled = true; stopDjVoice(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, pos]);

  const toggle = useCallback(() => {
    const el = audioRef.current; if (!el) return;
    if (el.paused) {
      // Restaure le volume au cas où un fade précédent l'aurait laissé à 0.
      if (el.volume < 0.05) el.volume = volume;
      el.play().then(() => setPlaying(true)).catch((e) => {
        console.warn("play failed", e);
        setPlaying(false);
      });
    } else {
      el.pause();
      setPlaying(false);
    }
  }, [volume]);

  const autoradioLoadingRef = useRef(false);

  const advance = useCallback(async (delta: number) => {
    if (!queue.length) return;
    const el = audioRef.current;
    if (repeat === "one" && delta > 0 && el) {
      el.currentTime = 0;
      void el.play();
      return;
    }
    const nextPos = pos + delta;
    if (nextPos < 0) { setPos(0); bumpReload((x) => x + 1); return; }
    if (nextPos < order.length) { setPos(nextPos); return; }

    // ---- reached the end of the queue ----
    // repeat=all → replay from the top (shuffle-aware).
    if (repeat === "all") {
      if (shuffle && queue.length > 1) {
        const cur = order[order.length - 1] ?? 0;
        const rest = shuffled(queue.map((_, i) => i).filter((i) => i !== cur));
        setOrder(rest);
        setPos(0);
        bumpReload((x) => x + 1);
      } else {
        setPos(0);
        bumpReload((x) => x + 1);
      }
      return;
    }

    // repeat=off → fetch similar tracks and keep the music going ("auto-radio").
    if (autoradioLoadingRef.current) return;
    autoradioLoadingRef.current = true;
    try {
      const last = queue[order[order.length - 1] ?? 0];
      if (!last) { autoradioLoadingRef.current = false; return; }
      type Sim = { id: string; title: string; duration_ms: number;
                   artist: { id: string; name: string };
                   album: { id: string; title: string; image_url: string | null } };
      const similar = await api<Sim[]>(`/api/recommend/tracks/similar/${last.id}?limit=10`);
      if (!similar.length) {
        // nothing more to play — loop the queue as a last resort
        setPos(0); bumpReload((x) => x + 1);
        return;
      }
      const asTracks: Track[] = similar
        .filter((s) => !queue.some((q) => q.id === s.id))
        .map((s) => ({
          id: s.id, title: s.title, disc: 1, track_no: 0,
          duration_ms: s.duration_ms, explicit: false, codec: null, bitrate_kbps: null,
          artist: { id: s.artist.id, name: s.artist.name, cover_id: null, image_url: null },
          album: { id: s.album.id, title: s.album.title, year: null,
                   cover_id: s.album.image_url, image_url: s.album.image_url,
                   artist: { id: s.artist.id, name: s.artist.name, cover_id: null, image_url: null } },
        }));
      if (!asTracks.length) { setPos(0); bumpReload((x) => x + 1); return; }
      const newQueue = [...queue, ...asTracks];
      const startIdx = queue.length; // first new track
      setQueue(newQueue);
      setOrder((o) => [...o, ...asTracks.map((_, i) => startIdx + i)]);
      setPos(order.length); // advance into the freshly-appended slot
    } finally {
      autoradioLoadingRef.current = false;
    }
  }, [queue, order, pos, repeat, shuffle]);

  const advanceRef = useRef(advance);
  useEffect(() => { advanceRef.current = advance; }, [advance]);
  const next = useCallback(() => advance(1), [advance]);
  const prev = useCallback(() => {
    const el = audioRef.current;
    if (el && el.currentTime > 3) { el.currentTime = 0; return; }
    advance(-1);
  }, [advance]);

  const endedRef = useRef<() => void>(() => {});
  useEffect(() => { endedRef.current = next; }, [next]);

  const seek = useCallback((sec: number) => { const el = audioRef.current; if (el) el.currentTime = sec; }, []);
  const enqueue = useCallback((t: Track) => {
    setQueue((q) => {
      const newQ = [...q, t];
      setOrder((o) => [...o, newQ.length - 1]);
      return newQ;
    });
  }, []);

  const setShuffle = useCallback((s: boolean) => {
    setShuffleState(s);
    setOrder((o) => {
      if (!queue.length) return o;
      const cur = o[pos] ?? 0;
      if (s) {
        // put current first, then shuffle the rest
        const rest = shuffled(queue.map((_, i) => i).filter((i) => i !== cur));
        setPos(0);
        return [cur, ...rest];
      } else {
        // restore ascending order, keeping current position; smart-shuffle
        // recommendations aren't part of the original list, drop them
        const recs = recIdsRef.current;
        const seq = queue.map((_, i) => i).filter((i) => i === cur || !recs.has(queue[i]?.id));
        setPos(Math.max(0, seq.indexOf(cur)));
        return seq;
      }
    });
    if (!s) { setSmartState(false); smartGenRef.current++; }
  }, [queue, pos]);

  const cycleShuffle = useCallback(() => {
    if (!shuffle) { setSmartState(false); setShuffle(true); return; }
    if (!smartShuffle) {
      setSmartState(true);
      if (queueSourceRef.current !== "dj") {
        const recs = recIdsRef.current;
        void injectSmart(queueRef.current.filter((t) => !recs.has(t.id)));
      }
      return;
    }
    setShuffle(false);
  }, [shuffle, smartShuffle, setShuffle, injectSmart]);

  const isRecommended = useCallback((id: string) => recIds.has(id), [recIds]);

  const removeRecommended = useCallback((id: string) => {
    addNotInterested(id);
    const cur = queueRef.current[orderRef.current[posRef.current]];
    if (cur?.id === id) {
      void advanceRef.current(1);
      return;
    }
    setOrder((o) => o.filter((idx, i) => i <= posRef.current || queueRef.current[idx]?.id !== id));
  }, []);

  const upNext = useMemo(
    () => order.slice(pos + 1, pos + 51).map((i) => queue[i]).filter(Boolean) as Track[],
    [order, pos, queue],
  );

  const value = useMemo<PlayerCtx>(() => ({
    current, queue, index, playing, duration, volume, repeat, shuffle,
    play, playOne, playDJMix, toggle, next, prev, seek,
    setVolume: setVol, setRepeat, setShuffle, enqueue,
    smartShuffle, cycleShuffle, playSmartShuffle, isRecommended, removeRecommended, upNext,
    crossfadeMs, setCrossfadeMs,
  }), [current, queue, index, playing, duration, volume, repeat, shuffle,
      play, playOne, playDJMix, toggle, next, prev, seek, setShuffle, enqueue,
      smartShuffle, cycleShuffle, playSmartShuffle, isRecommended, removeRecommended, upNext,
      crossfadeMs, setCrossfadeMs]);

  return (
    <PlayerContext.Provider value={value}>
      <PlayerProgressContext.Provider value={progress}>
        {children}
      </PlayerProgressContext.Provider>
    </PlayerContext.Provider>
  );
}
